/**
 * Pure helpers for the Reports UI. Money values from the reports API are
 * decimal strings; these helpers only format them for display and never do
 * arithmetic on them (totals always come from the server).
 */

import * as types from '../../api/types';
import { invoiceDate, invoiceMoney } from '../invoiceUi';

export const REPORT_PAGE_SIZE = 20;

export interface ReportItem {
  to: string;
  label: string;
  description: string;
}

/** Cards on /reports. Descriptions follow the intent in docs/features/reports.md. */
export const REPORT_ITEMS: ReportItem[] = [
  { to: '/reports/trial-balance', label: 'Trial balance', description: 'Are the books balanced? Debits, credits and balance per GL account.' },
  { to: '/reports/ar-aging', label: 'AR aging', description: 'Who owes us, and how overdue is it? Open balances by customer and age.' },
  { to: '/reports/revenue', label: 'Revenue', description: 'What did we earn, and from whom? Invoiced revenue and tax by item type and customer.' },
  { to: '/reports/jobs', label: 'Jobs', description: 'How is field work going? Job counts by status and property, with the job list.' },
  { to: '/reports/payments', label: 'Payments', description: 'What came in, and how was it paid? Payment totals by method, with the payment list.' },
  { to: '/reports/customer-satisfaction', label: 'Customer satisfaction', description: 'How happy are customers? Average rating, response rate, trend over time and scores per technician.' },
];

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** A YYYY-MM-DD query value, or '' when missing or malformed (e.g. a hand-edited URL). */
export function dateParam(value: string | null | undefined): string {
  return value && DATE_ONLY.test(value) ? value : '';
}

/** Page number from the query string; anything invalid is page 1. */
export function pageParam(value: string | null | undefined): number {
  const page = Number(value);
  return Number.isInteger(page) && page > 1 ? page : 1;
}

/**
 * Validation message for a start/end pair, or null when the range is usable.
 * YYYY-MM-DD strings compare correctly as strings, which avoids time-zone
 * drift from Date parsing. Open-ended ranges are allowed.
 */
export function dateRangeError(startDate: string, endDate: string): string | null {
  if (startDate && endDate && startDate > endDate) return 'Start date must be on or before the end date.';
  return null;
}

export const reportMoney = (value: string | number | null | undefined): string =>
  value === null || value === undefined || value === '' ? '—' : invoiceMoney(value);

export const reportDate = (value: string | null | undefined): string => invoiceDate(value);

/** SCREAMING_SNAKE enum value to "Title Case" ("BANK_TRANSFER" -> "Bank Transfer"). */
export function enumLabel(value: string | null | undefined): string {
  if (!value) return '—';
  return value.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (letter) => letter.toUpperCase());
}

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  CASH: 'Cash',
  CHECK: 'Check',
  CC: 'Credit card',
  BANK_TRANSFER: 'Bank transfer',
};

export function paymentMethodLabel(method: string): string {
  return PAYMENT_METHOD_LABELS[method] || enumLabel(method);
}

const AGING_BUCKET_LABELS: Record<types.ARAgingBucket, string> = {
  current: 'Current',
  '1-30': '1–30 days',
  '31-60': '31–60 days',
  '61-90': '61–90 days',
  '90+': '90+ days',
};

export function agingBucketLabel(bucket: string): string {
  return AGING_BUCKET_LABELS[bucket as types.ARAgingBucket] || bucket;
}

/** Route for the source document of a statement line. */
export function statementLinePath(line: Pick<types.CustomerStatementLine, 'type' | 'referenceId'>): string {
  return line.type === 'PAYMENT' ? `/payments/${line.referenceId}` : `/invoices/${line.referenceId}`;
}

/** Display for a decimal string that is exactly zero ("0", "0.00", "-0.00"). */
export function isZeroAmount(value: string | null | undefined): boolean {
  return !value || /^-?0*(\.0*)?$/.test(value.trim());
}

/** Assignee display for a job report row: email when known, otherwise the id; null is "Unassigned". */
export function reportAssignee(assignedTo: string | null, emailsById: Record<string, string>): string {
  if (!assignedTo) return 'Unassigned';
  return emailsById[assignedTo] || assignedTo;
}
